import './styles.css';
import { type AppState } from '../state/appState';
import { appConfig } from '../config/appConfig';
import { renderIntroScreen } from './screens/IntroScreen';
import { renderPermissionPrepScreen } from './screens/PermissionPrepScreen';
import { renderPermissionErrorScreen } from './screens/PermissionErrorScreen';
import { renderUnsupportedScreen } from './screens/UnsupportedScreen';
import { renderARScreen, teardownARSession } from './screens/ARScreen';
import { mountScanningOverlay } from './overlays/ScanningOverlay';
import { setInstruction } from './overlays/InstructionOverlay';
import { mountWhaleOverlay, showInfoModal } from './overlays/InfoModal';
import { mountShutterButton } from './overlays/ShutterButton';
import { stopARSession } from '../ar/sessionManager';

// ── AR state set ──────────────────────────────────────────────────────────────
// States where the AR shell is the persistent backdrop; only the overlay slot
// content changes rather than the whole root being replaced.

const AR_SHELL_STATES = new Set<AppState>([
  'AR_STARTING',
  'SCANNING',
  'SCAN_GUIDANCE',
  'ANCHORED_AWAITING_AIM',
  'WHALE_VISIBLE',
  'INFO_MODAL_OPEN',
  'TRACKING_LOST',
]);

// Cleanup function for the current overlay — called before mounting the next.
let overlayCleanup: (() => void) | null = null;

function runOverlayCleanup(): void {
  if (overlayCleanup) {
    overlayCleanup();
    overlayCleanup = null;
  }
}

/**
 * Returns the #ar-overlay-slot.
 * The AR shell must already exist when any AR overlay state is entered
 * (always via AR_STARTING first), so this is just a safe lookup.
 */
function getOverlaySlot(root: HTMLElement): HTMLElement {
  return root.querySelector<HTMLElement>('#ar-overlay-slot')!;
}

export function renderApp(root: HTMLElement, state: AppState): void {
  // Leaving AR entirely — clean up overlays and stop the camera stream.
  if (!AR_SHELL_STATES.has(state)) {
    runOverlayCleanup();
    // Only stop the session if we're actually leaving (shell is present).
    if (root.querySelector('#ar-shell')) {
      teardownARSession(root);
    }
  }

  switch (state) {
    // ── Non-AR screens ──────────────────────────────────────────────────────
    case 'INTRO':
      renderIntroScreen(root);
      break;

    case 'PERMISSION_PREP':
      renderPermissionPrepScreen(root);
      break;

    case 'PERMISSION_ERROR':
      renderPermissionErrorScreen(root);
      break;

    case 'UNSUPPORTED':
      renderUnsupportedScreen(root);
      break;

    // ── AR shell bootstrap ──────────────────────────────────────────────────
    case 'AR_STARTING':
      // Starts camera; on success timer fires arStarted() → SCANNING.
      // On failure calls deviceUnsupported() → UNSUPPORTED.
      renderARScreen(root);
      break;

    // ── AR overlay states (shell persists) ─────────────────────────────────

    case 'SCANNING': {
      runOverlayCleanup();
      const slot = getOverlaySlot(root);
      overlayCleanup = mountScanningOverlay(slot);
      break;
    }

    case 'SCAN_GUIDANCE': {
      runOverlayCleanup();
      const slot = getOverlaySlot(root);
      setInstruction(slot, appConfig.copy.scanGuidance);
      break;
    }

    case 'ANCHORED_AWAITING_AIM': {
      runOverlayCleanup();
      setInstruction(getOverlaySlot(root), appConfig.copy.aimInstruction);
      break;
    }

    case 'TRACKING_LOST': {
      runOverlayCleanup();
      setInstruction(getOverlaySlot(root), appConfig.copy.trackingLost);
      break;
    }

    case 'WHALE_VISIBLE': {
      runOverlayCleanup();
      const slot = getOverlaySlot(root);
      setInstruction(slot, null);
      const cleanupInfo    = mountWhaleOverlay(slot);
      const cleanupShutter = mountShutterButton(slot);
      overlayCleanup = () => { cleanupInfo(); cleanupShutter(); };
      break;
    }

    case 'INFO_MODAL_OPEN': {
      // Do NOT run overlay cleanup — the Info button must stay mounted.
      showInfoModal(getOverlaySlot(root));
      break;
    }

    case 'LOAD_ERROR': {
      // Camera may still be running; stop it before showing the error.
      stopARSession();
      root.innerHTML = `
        <div class="error-screen">
          <div class="error-screen__content">
            <p class="error-screen__message">${appConfig.copy.loadError}</p>
          </div>
        </div>`;
      break;
    }

    default: {
      const _exhaustive: never = state;
      void _exhaustive;
    }
  }
}
